/**
 * Global Scan Modal.
 *
 * A full-screen overlay scanner that can be triggered from anywhere in
 * the application. After scanning an asset, presents a context-aware
 * action menu based on the asset's current status.
 *
 * Scan modes:
 * - "lookup" (default) — scan → show asset summary + action menu
 * - "receive" — scan → mark PO line as received
 * - "move" — scan → prompt for new location
 * - "work_order" — scan → create or add to a work order
 * - "audit" — scan → confirm asset at expected location
 *
 * The modal can be opened globally via the useScanStore hook or the
 * scan button in the navigation bar.
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  ScanLine,
  Package,
  Wrench,
  ShoppingCart,
  MoveRight,
  ClipboardCheck,
  FileText,
  Eye,
  ChevronRight,
  MapPin,
  AlertCircle,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import BarcodeScanner from "@/features/scanner/BarcodeScanner";
import AssetStatusBadge from "@/features/inventory/components/AssetStatusBadge";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";

interface Asset {
  id: string;
  sku: string;
  name: string;
  status: string;
  category: string;
  location: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  is_on_hold: boolean;
}

interface ScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-set scan mode. Default is "lookup" (general purpose). */
  mode?: "lookup" | "receive" | "move" | "work_order" | "audit";
  /** For contextual modes — the entity this scan relates to (e.g., PO ID for receiving). */
  contextEntityId?: string;
}

const ACTIONS = [
  {
    key: "view",
    label: "View Details",
    icon: Eye,
    description: "Open the full asset detail page",
  },
  {
    key: "move",
    label: "Move Asset",
    icon: MoveRight,
    description: "Change the asset's location",
  },
  {
    key: "work_order",
    label: "Create Work Order",
    icon: Wrench,
    description: "Start a new work order for this asset",
  },
  {
    key: "inspect",
    label: "Start Inspection",
    icon: ClipboardCheck,
    description: "Begin an inspection form",
  },
  {
    key: "add_to_sale",
    label: "Add to Sale",
    icon: ShoppingCart,
    description: "Add to a sales order or quote",
  },
  {
    key: "add_to_po",
    label: "View Purchase Order",
    icon: FileText,
    description: "View the linked purchase order",
  },
];

function ScanModal({ isOpen, onClose, mode = "lookup" }: ScanModalProps) {
  const navigate = useNavigate();
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [matchedAsset, setMatchedAsset] = useState<Asset | null>(null);

  // Search for the scanned code.
  const { data: searchResults, isLoading } = useQuery<{ items: Asset[] }>({
    queryKey: ["scan-lookup", scannedCode],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/assets?search=${encodeURIComponent(scannedCode!)}&page_size=5`,
        )
      ).data,
    enabled: !!scannedCode && !matchedAsset,
  });

  // Auto-match on exact SKU hit.
  if (
    scannedCode &&
    searchResults &&
    !isLoading &&
    !matchedAsset &&
    !notFound
  ) {
    const exact = searchResults.items.find(
      (a) => a.sku === scannedCode || a.sku === scannedCode.toUpperCase(),
    );
    if (exact) {
      setMatchedAsset(exact);
    } else if (searchResults.items.length === 0) {
      setNotFound(true);
    }
  }

  const handleScan = useCallback((code: string) => {
    setScannedCode(code);
    setNotFound(false);
    setMatchedAsset(null);
  }, []);

  const resetScan = () => {
    setScannedCode(null);
    setNotFound(false);
    setMatchedAsset(null);
  };

  const handleAction = (action: string) => {
    if (!matchedAsset) return;
    onClose();

    switch (action) {
      case "view":
        navigate(`/inventory/${matchedAsset.id}`);
        break;
      case "move":
        navigate(`/inventory/${matchedAsset.id}?action=move`);
        break;
      case "work_order":
        navigate(`/work-orders/new?asset_id=${matchedAsset.id}`);
        break;
      case "inspect":
        navigate(`/inventory/${matchedAsset.id}?tab=forms`);
        break;
      case "add_to_sale":
        navigate(`/sales/new?asset_id=${matchedAsset.id}`);
        break;
      case "add_to_po":
        navigate(`/inventory/${matchedAsset.id}`);
        break;
      default:
        navigate(`/inventory/${matchedAsset.id}`);
    }
  };

  if (!isOpen) return null;

  const modeTitle = {
    lookup: "Scan Asset",
    receive: "Scan to Receive",
    move: "Scan to Move",
    work_order: "Scan for Work Order",
    audit: "Scan to Audit",
  }[mode];

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md mt-16 mx-4 animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between rounded-t-[var(--radius-xl)] bg-[var(--surface-base)] border border-[var(--border-default)] border-b-0 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-interactive-primary)]/10">
              <ScanLine className="h-5 w-5 text-[var(--color-interactive-primary)]" />
            </div>
            <div>
              <h2 className="text-base font-bold text-[var(--text-primary)]">
                {modeTitle}
              </h2>
              <p className="text-xs text-[var(--text-tertiary)]">
                {matchedAsset
                  ? "Asset found — choose an action"
                  : "Point camera at a barcode or QR code"}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              resetScan();
              onClose();
            }}
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--text-primary)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="rounded-b-[var(--radius-xl)] bg-[var(--surface-base)] border border-[var(--border-default)] border-t-0 px-5 pb-5 max-h-[calc(100vh-10rem)] overflow-y-auto">
          {/* Scanner (only shown when no asset matched yet) */}
          {!matchedAsset && !notFound && (
            <div className="mb-4">
              <BarcodeScanner onScan={handleScan} />
            </div>
          )}

          {/* Loading indicator */}
          {isLoading && scannedCode && (
            <div className="py-6 text-center text-sm text-[var(--text-secondary)]">
              Looking up "{scannedCode}"...
            </div>
          )}

          {/* Not Found */}
          {notFound && (
            <div className="py-6 text-center">
              <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
              <p className="mt-3 text-sm font-semibold text-[var(--text-primary)]">
                No asset found
              </p>
              <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                No asset matches "{scannedCode}". Check the barcode and try
                again.
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-4"
                onClick={resetScan}
              >
                <ScanLine className="h-3.5 w-3.5" /> Scan Again
              </Button>
            </div>
          )}

          {/* Multiple results (no exact match) */}
          {scannedCode &&
            searchResults &&
            searchResults.items.length > 0 &&
            !matchedAsset &&
            !notFound && (
              <div>
                <p className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.1em] mb-2">
                  Possible matches
                </p>
                <div className="space-y-1.5">
                  {searchResults.items.map((asset) => (
                    <button
                      key={asset.id}
                      onClick={() => setMatchedAsset(asset)}
                      className="flex w-full items-center gap-3 rounded-[var(--radius-md)] border border-[var(--border-default)] p-3 text-left hover:bg-[var(--color-surface-hover)] transition-colors"
                    >
                      <Package className="h-5 w-5 text-[var(--text-tertiary)] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--text-primary)]">
                          {asset.name}
                        </p>
                        <p className="text-[11px] text-[var(--text-tertiary)] font-mono">
                          {asset.sku}
                        </p>
                      </div>
                      <AssetStatusBadge status={asset.status} />
                    </button>
                  ))}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-3 w-full"
                  onClick={resetScan}
                >
                  Scan Again
                </Button>
              </div>
            )}

          {/* ═══ Matched Asset — Summary + Action Menu ═══ */}
          {matchedAsset && (
            <div className="space-y-4">
              {/* Asset Summary Card */}
              <div className="rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-base font-bold text-[var(--text-primary)]">
                      {matchedAsset.name}
                    </h3>
                    <p className="text-xs font-mono text-brand-600 mt-0.5">
                      {matchedAsset.sku}
                    </p>
                  </div>
                  <AssetStatusBadge status={matchedAsset.status} />
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
                  <span className="capitalize">{matchedAsset.category}</span>
                  {matchedAsset.year && <span>{matchedAsset.year}</span>}
                  {matchedAsset.make && <span>{matchedAsset.make}</span>}
                  {matchedAsset.model && <span>{matchedAsset.model}</span>}
                </div>
                {matchedAsset.location && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-[var(--text-tertiary)]">
                    <MapPin className="h-3 w-3" /> {matchedAsset.location}
                  </div>
                )}
                {matchedAsset.is_on_hold && (
                  <Badge variant="warning" className="mt-2">
                    On Hold
                  </Badge>
                )}
              </div>

              {/* Action Menu */}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-2">
                  Quick Actions
                </p>
                <div className="space-y-1">
                  {ACTIONS.map((action) => (
                    <button
                      key={action.key}
                      onClick={() => handleAction(action.key)}
                      className="flex w-full items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-left hover:bg-[var(--color-surface-hover)] transition-colors group"
                    >
                      <action.icon className="h-4 w-4 text-[var(--text-tertiary)] group-hover:text-[var(--color-interactive-primary)] transition-colors shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--text-primary)]">
                          {action.label}
                        </p>
                        <p className="text-[11px] text-[var(--text-tertiary)]">
                          {action.description}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[var(--text-tertiary)] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Scan Another */}
              <Button
                size="sm"
                variant="secondary"
                className="w-full"
                onClick={resetScan}
              >
                <ScanLine className="h-3.5 w-3.5" /> Scan Another Asset
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScanModal;
